import React, { useState, useEffect } from 'react'
import { 
  ArrowLeft,
  Save,
  RotateCcw,
  Banknote
} from 'lucide-react'
import { Breadcrumb } from '../../components/Common'
import { useNavigate, useLocation } from 'react-router-dom'
import { toast } from 'react-toastify'
import expenseReimbursementService from '../../services/expenseReimbursementService'
import paymentMethodMasterService from '../../services/paymentMethodMasterService'
import '../../styles/expenseClaimForm.css'

const ExpenseReimbursementForm = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const isEdit = location.pathname.includes('/edit')
  const isView = location.pathname.includes('/view')
  const reimbursementData = location.state?.reimbursementData

  const [formData, setFormData] = useState({
    reimbursementNumber: '',
    expenseClaim: '',
    claimNumber: '',
    employee: '',
    employeeName: '',
    employeeCode: '',
    department: '',
    reimbursementDate: new Date().toISOString().split('T')[0],
    claimAmount: 0,
    approvedAmount: 0,
    reimbursementAmount: 0,
    currency: 'INR',
    paymentMethod: '',
    paymentMethodName: '',
    paymentDate: '',
    paymentReference: '',
    paymentTransactionId: '',
    bankAccountNumber: '',
    bankName: '',
    ifscCode: '',
    accountHolderName: '',
    status: 'Pending',
    notes: ''
  })

  const [isLoadingReimbursementNumber, setIsLoadingReimbursementNumber] = useState(true)
  const [approvedClaims, setApprovedClaims] = useState([])
  const [paymentMethods, setPaymentMethods] = useState([])
  const [loading, setLoading] = useState(false)

  // Load initial data
  useEffect(() => {
    if (!isEdit && !isView) {
      fetchReimbursementNumber()
      loadApprovedClaims()
    } else if (reimbursementData) {
      // Populate form with existing data
      setFormData({
        reimbursementNumber: reimbursementData.reimbursementNumber || '',
        expenseClaim: reimbursementData.expenseClaim?._id || reimbursementData.expenseClaim || '',
        claimNumber: reimbursementData.claimNumber || '',
        employee: reimbursementData.employee?._id || reimbursementData.employee || '',
        employeeName: reimbursementData.employeeName || '',
        employeeCode: reimbursementData.employeeCode || '',
        department: reimbursementData.department || '',
        reimbursementDate: reimbursementData.reimbursementDate ? new Date(reimbursementData.reimbursementDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
        claimAmount: reimbursementData.claimAmount || 0,
        approvedAmount: reimbursementData.approvedAmount || 0,
        reimbursementAmount: reimbursementData.reimbursementAmount || 0,
        currency: reimbursementData.currency || 'INR',
        paymentMethod: reimbursementData.paymentMethod?._id || reimbursementData.paymentMethod || '',
        paymentMethodName: reimbursementData.paymentMethodName || '',
        paymentDate: reimbursementData.paymentDate ? new Date(reimbursementData.paymentDate).toISOString().split('T')[0] : '',
        paymentReference: reimbursementData.paymentReference || '',
        paymentTransactionId: reimbursementData.paymentTransactionId || '',
        bankAccountNumber: reimbursementData.bankAccountNumber || '',
        bankName: reimbursementData.bankName || '',
        ifscCode: reimbursementData.ifscCode || '',
        accountHolderName: reimbursementData.accountHolderName || '',
        status: reimbursementData.status || 'Pending',
        notes: reimbursementData.notes || ''
      })
      setIsLoadingReimbursementNumber(false)
    }

    // Load dropdown data
    loadDropdownData()
  }, [])

  const fetchReimbursementNumber = async () => {
    try {
      setIsLoadingReimbursementNumber(true)
      const response = await expenseReimbursementService.getNextReimbursementNumber()
      if (response.success && response.data) {
        setFormData(prev => ({
          ...prev,
          reimbursementNumber: response.data.reimbursementNumber || response.data.formattedValue || 'REIMB0001'
        }))
      }
    } catch (error) {
      console.error('Error fetching reimbursement number:', error)
      setFormData(prev => ({ ...prev, reimbursementNumber: 'REIMB0001' }))
    } finally {
      setIsLoadingReimbursementNumber(false)
    }
  }

  const loadApprovedClaims = async () => {
    try {
      const response = await expenseReimbursementService.getApprovedClaimsForReimbursement()
      if (response.success) {
        setApprovedClaims(response.data || [])
      }
    } catch (error) {
      console.error('Error loading approved claims:', error)
    }
  }

  const loadDropdownData = async () => {
    try {
      const paymentMethodsRes = await paymentMethodMasterService.getActivePaymentMethods()
      if (paymentMethodsRes.success) {
        setPaymentMethods(paymentMethodsRes.data || [])
      }
    } catch (error) {
      console.error('Error loading dropdown data:', error)
    }
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))

    // When expense claim is selected, populate employee and amount details
    if (name === 'expenseClaim') {
      const selectedClaim = approvedClaims.find(claim => claim._id === value)
      if (selectedClaim) {
        setFormData(prev => ({
          ...prev,
          claimNumber: selectedClaim.claimNumber || '',
          employee: selectedClaim.employee?._id || selectedClaim.employee || '',
          employeeName: selectedClaim.employeeName || selectedClaim.employee?.employeeName || '',
          employeeCode: selectedClaim.employeeCode || selectedClaim.employee?.employeeCode || '',
          department: selectedClaim.department || selectedClaim.employee?.department || '',
          claimAmount: selectedClaim.grandTotal || 0,
          approvedAmount: selectedClaim.grandTotal || 0,
          reimbursementAmount: selectedClaim.grandTotal || 0,
          currency: selectedClaim.currency || 'INR'
        }))
      }
    }

    // When payment method is selected, populate payment method name
    if (name === 'paymentMethod') {
      const selectedMethod = paymentMethods.find(method => method._id === value)
      if (selectedMethod) {
        setFormData(prev => ({
          ...prev,
          paymentMethodName: selectedMethod.paymentMethodName || ''
        }))
      }
    }
  }

  const handleSave = async () => {
    try {
      // Validation
      if (!formData.expenseClaim) {
        toast.error('Please select an approved expense claim')
        return
      }
      if (!formData.paymentMethod) {
        toast.error('Please select a payment method')
        return
      }
      if (formData.reimbursementAmount <= 0) {
        toast.error('Reimbursement amount must be greater than 0')
        return
      }

      setLoading(true)
      let response
      if (isEdit) {
        response = await expenseReimbursementService.updateReimbursement(reimbursementData._id, formData)
      } else {
        response = await expenseReimbursementService.createReimbursement(formData)
      }

      if (response.success) {
        toast.success(isEdit ? 'Reimbursement updated successfully!' : 'Reimbursement created successfully!')
        navigate('/expenses/expense-reimbursement')
      } else {
        toast.error(response.message || 'Failed to save reimbursement')
      }
    } catch (error) {
      console.error('Error saving reimbursement:', error)
      if (!error._errorHandled) {
        if (error.response?.status === 400) {
          toast.error(error.response.data?.message || 'Invalid data. Please check your inputs.')
        } else {
          toast.error('Failed to save reimbursement. Please try again.')
        }
      }
    } finally {
      setLoading(false)
    }
  }

  const handleReset = () => {
    if (isEdit || isView) {
      // Reset to original data
      if (reimbursementData) {
        setFormData({
          reimbursementNumber: reimbursementData.reimbursementNumber || '',
          expenseClaim: reimbursementData.expenseClaim?._id || reimbursementData.expenseClaim || '',
          claimNumber: reimbursementData.claimNumber || '',
          employee: reimbursementData.employee?._id || reimbursementData.employee || '',
          employeeName: reimbursementData.employeeName || '',
          employeeCode: reimbursementData.employeeCode || '',
          department: reimbursementData.department || '',
          reimbursementDate: reimbursementData.reimbursementDate ? new Date(reimbursementData.reimbursementDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
          claimAmount: reimbursementData.claimAmount || 0,
          approvedAmount: reimbursementData.approvedAmount || 0,
          reimbursementAmount: reimbursementData.reimbursementAmount || 0,
          currency: reimbursementData.currency || 'INR',
          paymentMethod: reimbursementData.paymentMethod?._id || reimbursementData.paymentMethod || '',
          paymentMethodName: reimbursementData.paymentMethodName || '',
          paymentDate: reimbursementData.paymentDate ? new Date(reimbursementData.paymentDate).toISOString().split('T')[0] : '',
          paymentReference: reimbursementData.paymentReference || '',
          paymentTransactionId: reimbursementData.paymentTransactionId || '',
          bankAccountNumber: reimbursementData.bankAccountNumber || '',
          bankName: reimbursementData.bankName || '',
          ifscCode: reimbursementData.ifscCode || '',
          accountHolderName: reimbursementData.accountHolderName || '',
          status: reimbursementData.status || 'Pending',
          notes: reimbursementData.notes || ''
        })
      }
    } else {
      // Reset to initial state
      setFormData({
        reimbursementNumber: formData.reimbursementNumber, // Keep the reimbursement number
        expenseClaim: '',
        claimNumber: '',
        employee: '',
        employeeName: '',
        employeeCode: '',
        department: '',
        reimbursementDate: new Date().toISOString().split('T')[0],
        claimAmount: 0,
        approvedAmount: 0,
        reimbursementAmount: 0,
        currency: 'INR',
        paymentMethod: '',
        paymentMethodName: '',
        paymentDate: '',
        paymentReference: '',
        paymentTransactionId: '',
        bankAccountNumber: '',
        bankName: '',
        ifscCode: '',
        accountHolderName: '',
        status: 'Pending',
        notes: ''
      })
    }
  }

  const formatCurrency = (amount, currency = 'INR') => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2
    }).format(amount || 0)
  }

  const breadcrumbItems = [
    { label: 'Expenses', path: '/expenses', onClick: () => navigate('/expenses') },
    { label: 'Reimbursements', path: '/expenses/expense-reimbursement', onClick: () => navigate('/expenses/expense-reimbursement') },
    { label: isEdit ? 'Edit Reimbursement' : isView ? 'View Reimbursement' : 'New Reimbursement', path: location.pathname, isCurrent: true }
  ]

  return (
    <div className="sales-dashboard">
      <div className="breadcrumb-section">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      <div className="page-header">
        <div className="page-title">
          <h1>Reimbursement</h1>
          <p>{isEdit ? 'Edit reimbursement' : isView ? 'View reimbursement' : 'Create new reimbursement'}</p>
        </div>
        <div className="page-actions">
          <button className="btn btn-secondary" onClick={() => navigate('/expenses/expense-reimbursement')}>
            <ArrowLeft size={16} />
            Back
          </button>
        </div>
      </div>

      <div className="form-container">
        <div className="sac-form-header">
          <h2>Reimbursement Information</h2>
        </div>
          
        <form className="form-content">
          <div className="section-header">
            <h3>Reimbursement Details</h3>
          </div>

          {/* Row 1: Reimbursement Number, Expense Claim, Reimbursement Date */}
          <div className="grid-row-4">
            <div className="form-group">
              <label htmlFor="reimbursementNumber" className="form-label required">
                Reimbursement Number
              </label>
              <input
                type="text"
                id="reimbursementNumber"
                name="reimbursementNumber"
                value={isLoadingReimbursementNumber ? 'Generating...' : formData.reimbursementNumber}
                readOnly
                className="form-input"
                style={{ backgroundColor: '#f9fafb', color: 'var(--color-text-secondary, #5B6478)', cursor: 'not-allowed' }}
              />
            </div>

            <div className="form-group">
              <label htmlFor="expenseClaim" className="form-label required">
                Approved Claim
              </label>
              <select
                id="expenseClaim"
                name="expenseClaim"
                value={formData.expenseClaim}
                onChange={handleInputChange}
                className="form-input"
                required
                disabled={isView || isEdit}
              >
                <option value="">Select Approved Claim</option>
                {approvedClaims.map(claim => (
                  <option key={claim._id} value={claim._id}>
                    {claim.claimNumber} - {claim.employeeName} - {formatCurrency(claim.grandTotal, claim.currency)}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="claimNumber" className="form-label">
                Claim Number
              </label>
              <input
                type="text"
                id="claimNumber"
                name="claimNumber"
                value={formData.claimNumber}
                readOnly
                className="form-input"
                style={{ backgroundColor: '#f9fafb', color: 'var(--color-text-secondary, #5B6478)', cursor: 'not-allowed' }}
              />
            </div>

            <div className="form-group">
              <label htmlFor="reimbursementDate" className="form-label required">
                Reimbursement Date
              </label>
              <input
                type="date"
                id="reimbursementDate"
                name="reimbursementDate"
                value={formData.reimbursementDate}
                onChange={handleInputChange}
                className="form-input"
                required
                disabled={isView}
              />
            </div>
          </div>

          {/* Row 2: Employee Information (Read-only) */}
          <div className="grid-row-4">
            <div className="form-group">
              <label htmlFor="employeeName" className="form-label">
                Employee Name
              </label>
              <input
                type="text"
                id="employeeName"
                name="employeeName"
                value={formData.employeeName}
                readOnly
                className="form-input"
                style={{ backgroundColor: '#f9fafb', color: 'var(--color-text-secondary, #5B6478)', cursor: 'not-allowed' }}
              />
            </div>

            <div className="form-group">
              <label htmlFor="employeeCode" className="form-label">
                Employee Code
              </label>
              <input
                type="text"
                id="employeeCode"
                name="employeeCode"
                value={formData.employeeCode}
                readOnly
                className="form-input"
                style={{ backgroundColor: '#f9fafb', color: 'var(--color-text-secondary, #5B6478)', cursor: 'not-allowed' }}
              />
            </div>

            <div className="form-group">
              <label htmlFor="department" className="form-label">
                Department
              </label>
              <input
                type="text"
                id="department"
                name="department"
                value={formData.department}
                readOnly
                className="form-input"
                style={{ backgroundColor: '#f9fafb', color: 'var(--color-text-secondary, #5B6478)', cursor: 'not-allowed' }}
              />
            </div>

            <div className="form-group">
              <label htmlFor="currency" className="form-label">
                Currency
              </label>
              <input
                type="text"
                id="currency"
                name="currency"
                value={formData.currency}
                readOnly
                className="form-input"
                style={{ backgroundColor: '#f9fafb', color: 'var(--color-text-secondary, #5B6478)', cursor: 'not-allowed' }}
              />
            </div>
          </div>

          {/* Row 3: Amount Information */}
          <div className="grid-row-4">
            <div className="form-group">
              <label htmlFor="claimAmount" className="form-label">
                Claim Amount
              </label>
              <input
                type="text"
                id="claimAmount"
                name="claimAmount"
                value={formatCurrency(formData.claimAmount, formData.currency)}
                readOnly
                className="form-input"
                style={{ backgroundColor: '#f9fafb', color: 'var(--color-text-secondary, #5B6478)', cursor: 'not-allowed', fontWeight: '600' }}
              />
            </div>

            <div className="form-group">
              <label htmlFor="approvedAmount" className="form-label">
                Approved Amount
              </label>
              <input
                type="text"
                id="approvedAmount"
                name="approvedAmount"
                value={formatCurrency(formData.approvedAmount, formData.currency)}
                readOnly
                className="form-input"
                style={{ backgroundColor: '#f9fafb', color: 'var(--color-text-secondary, #5B6478)', cursor: 'not-allowed', fontWeight: '600' }}
              />
            </div>

            <div className="form-group">
              <label htmlFor="reimbursementAmount" className="form-label required">
                Reimbursement Amount
              </label>
              <input
                type="number"
                id="reimbursementAmount"
                name="reimbursementAmount"
                value={formData.reimbursementAmount}
                onChange={handleInputChange}
                className="form-input"
                required
                min="0"
                step="0.01"
                disabled={isView}
              />
            </div>

            <div className="form-group">
              <label htmlFor="status" className="form-label">
                Status
              </label>
              <input
                type="text"
                id="status"
                name="status"
                value={formData.status}
                readOnly
                className="form-input"
                style={{ backgroundColor: '#f9fafb', color: 'var(--color-text-secondary, #5B6478)', cursor: 'not-allowed' }}
              />
            </div>
          </div>

          {/* Payment Information Section */}
          <div className="form-section-divider">
            <div>
              <h2 className="section-title">Payment Information</h2>
              <p className="section-subtitle">Payment method and transaction details</p>
            </div>
          </div>

          <div className="grid-row-4">
            <div className="form-group">
              <label htmlFor="paymentMethod" className="form-label required">
                Payment Method
              </label>
              <select
                id="paymentMethod"
                name="paymentMethod"
                value={formData.paymentMethod}
                onChange={handleInputChange}
                className="form-input"
                required
                disabled={isView}
              >
                <option value="">Select Payment Method</option>
                {paymentMethods.map(method => (
                  <option key={method._id} value={method._id}>
                    {method.paymentMethodName}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="paymentDate" className="form-label">
                Payment Date
              </label>
              <input
                type="date"
                id="paymentDate"
                name="paymentDate"
                value={formData.paymentDate}
                onChange={handleInputChange}
                className="form-input"
                disabled={isView}
              />
            </div>

            <div className="form-group">
              <label htmlFor="paymentReference" className="form-label">
                Payment Reference
              </label>
              <input
                type="text"
                id="paymentReference"
                name="paymentReference"
                value={formData.paymentReference}
                onChange={handleInputChange}
                className="form-input"
                placeholder="Payment reference number"
                disabled={isView}
              />
            </div>

            <div className="form-group">
              <label htmlFor="paymentTransactionId" className="form-label">
                Transaction ID
              </label>
              <input
                type="text"
                id="paymentTransactionId"
                name="paymentTransactionId"
                value={formData.paymentTransactionId}
                onChange={handleInputChange}
                className="form-input"
                placeholder="Transaction ID"
                disabled={isView}
              />
            </div>
          </div>

          {/* Bank Account Information Section */}
          <div className="form-section-divider">
            <div>
              <h2 className="section-title">Bank Account Information</h2>
              <p className="section-subtitle">Employee bank account details for reimbursement</p>
            </div>
          </div>

          <div className="grid-row-4">
            <div className="form-group">
              <label htmlFor="bankAccountNumber" className="form-label">
                Account Number
              </label>
              <input
                type="text"
                id="bankAccountNumber"
                name="bankAccountNumber"
                value={formData.bankAccountNumber}
                onChange={handleInputChange}
                className="form-input"
                placeholder="Bank account number"
                disabled={isView}
              />
            </div>

            <div className="form-group">
              <label htmlFor="bankName" className="form-label">
                Bank Name
              </label>
              <input
                type="text"
                id="bankName"
                name="bankName"
                value={formData.bankName}
                onChange={handleInputChange}
                className="form-input"
                placeholder="Bank name"
                disabled={isView}
              />
            </div>

            <div className="form-group">
              <label htmlFor="ifscCode" className="form-label">
                IFSC Code
              </label>
              <input
                type="text"
                id="ifscCode"
                name="ifscCode"
                value={formData.ifscCode}
                onChange={handleInputChange}
                className="form-input"
                placeholder="IFSC code"
                disabled={isView}
              />
            </div>

            <div className="form-group">
              <label htmlFor="accountHolderName" className="form-label">
                Account Holder Name
              </label>
              <input
                type="text"
                id="accountHolderName"
                name="accountHolderName"
                value={formData.accountHolderName}
                onChange={handleInputChange}
                className="form-input"
                placeholder="Account holder name"
                disabled={isView}
              />
            </div>
          </div>

          {/* Notes Section */}
          <div className="form-group">
            <label htmlFor="notes" className="form-label">
              Notes
            </label>
            <textarea
              id="notes"
              name="notes"
              value={formData.notes}
              onChange={handleInputChange}
              className="form-input"
              rows="3"
              placeholder="Additional notes or comments"
              disabled={isView}
            />
          </div>

          {/* Form Actions */}
          {!isView && (
            <div className="form-actions">
              <button type="button" className="btn btn-secondary" onClick={handleReset}>
                <RotateCcw size={16} />
                Reset
              </button>
              <button type="button" className="btn btn-primary" onClick={handleSave} disabled={loading}>
                <Save size={16} />
                {loading ? 'Saving...' : isEdit ? 'Update' : 'Save'}
              </button>
            </div>
          )}
        </form>
      </div>
    </div>
  )
}

export default ExpenseReimbursementForm

